import type { InteractiveMode } from '@/types';
import type { ZoomAccessLevel } from '@/stores/setupStore';

/** Where the guided-setup browser lands before sign-in, and the sign-in page
 *  it navigates to once the user picks an access level. */
export const ZOOM_PORTAL_URL = 'https://www.zoom.com/';
export const ZOOM_SIGNIN_URL = 'https://zoom.us/signin';
/** Zoom Marketplace "Build App" page — where the Server-to-Server app is created. */
export const ZOOM_S2S_CREATE_URL = 'https://marketplace.zoom.us/develop/create';

/** Posted when ZAC takes over after sign-in to create the connection app.
 *  Deliberately short — the app-name prompt that follows carries the "what
 *  happens next" explanation, so this bubble doesn't duplicate it. */
export const ZOOM_TAKEOVER_MESSAGE = "You're signed in — taking it from here. Opening the app builder…";

/** Assistant prompt that opens the guided Zoom setup. */
export const ZOOM_SETUP_PROMPT =
  "Let's connect your Zoom account. First — what level of access should I set up?\n\n" +
  '- **Read-only** lets me review your configuration and run audits.\n' +
  '- **Read + write** also lets me apply changes on your behalf.';

/** The access-level choice buttons shown under the prompt. Both are real
 *  choices, so neither is highlighted: the user picks, ZAC does not steer. */
export const ZOOM_ACCESS_CHOICE: InteractiveMode = {
  type: 'choice',
  options: [
    { id: 'zoom-access-reader', label: 'Read-only', value: 'zoom-setup:reader' },
    { id: 'zoom-access-writer', label: 'Read + write', value: 'zoom-setup:writer' },
  ],
};

/** The user-facing label for a chosen access level (matches the buttons). */
export function zoomChoiceLabel(level: ZoomAccessLevel): string {
  return level === 'writer' ? 'Read + write' : 'Read-only';
}

/** Acknowledgment shown once the user picks an access level. */
export function zoomAckText(level: ZoomAccessLevel): string {
  const what = level === 'writer'
    ? "**read + write** access — one connection app with the scopes that let me read your configuration and apply the changes you ask for"
    : '**read-only** access';
  return `Got it — I'll set up ${what}. Opening Zoom's Server-to-Server app builder on the right — if you're already signed in I'll take it from here; otherwise sign in and I'll continue.`;
}

/** Shown when the connection app already exists and Zoom is connected — the only
 *  remaining step is granting read scopes. ZAC does NOT re-drive app creation
 *  here (that would make a duplicate app); it points the user at the Scopes tab. */
export const ZOOM_SCOPES_PENDING_MESSAGE =
  "Your **ZAC Connection App** is already created and connected — nice. ✅\n\n" +
  'The last step is granting the **read scopes** so I can read your Zoom configuration. ' +
  "Open your app's **Scopes** tab in the [Zoom Marketplace](https://marketplace.zoom.us/user/build), " +
  "add the read scopes (e.g. account, user, meeting, and report read scopes), and save. " +
  "Once they're granted I'll be able to read your configuration and run audits.";

/** Where the guided flow currently stands, inferred from the harness URL. */
export type SetupStep = 'signin' | 'builder' | 'app-editor' | 'elsewhere';

export interface SetupPosition {
  step: SetupStep;
  /** Short human label for the resume message ("you're on …"). */
  label: string;
  /** Where ZAC should navigate to continue, or null to stay on the page. */
  resumeNavigateUrl: string | null;
  /** True if the user must act first (e.g. finish signing in) — ZAC stays hands-off. */
  needsUser: boolean;
}

/**
 * Compute where the guided setup is from the harness's current URL, and what
 * ZAC should do to continue. Used when ZAC (re)takes control — after a user
 * "take over", or when resuming — so it picks up from the real current page
 * instead of blindly replaying. URL-level for now; element-level sub-step
 * detection (which tab, name filled, scopes added) comes with the provisioning
 * recipes.
 */
export function computeSetupPosition(url: string): SetupPosition {
  let u: URL | null = null;
  try {
    u = new URL(url);
  } catch {
    u = null; // about:blank / empty / unparseable
  }
  const host = u?.hostname.toLowerCase() ?? '';
  const path = u?.pathname.toLowerCase() ?? '';

  // Sign-in / login pages — the user must finish authenticating; hands off.
  const isZoomUs = host === 'zoom.us' || host.endsWith('.zoom.us');
  if (host === 'id.zoom.us' || (isZoomUs && (path.startsWith('/signin') || path.startsWith('/login')))) {
    return { step: 'signin', label: 'the Zoom sign-in page', resumeNavigateUrl: null, needsUser: true };
  }

  if (host === 'marketplace.zoom.us') {
    // Inside an existing app — creation already happened; continue from here
    // (next stop: scopes). This is the URL-level "skip what's already done".
    if (/^\/develop\/apps\/[^/]+/.test(path)) {
      return { step: 'app-editor', label: 'your app’s settings', resumeNavigateUrl: null, needsUser: false };
    }
    // The build / create surface.
    if (path.startsWith('/develop')) {
      return { step: 'builder', label: 'the app builder', resumeNavigateUrl: null, needsUser: false };
    }
    // On the Marketplace but NOT in the developer area (e.g. the public
    // "Discover apps" home). The S2S builder is auth-gated, so landing here
    // means it bounced us — the user isn't signed in. Backtrack to the sign-in
    // page and hand control over so they can authenticate.
    return {
      step: 'signin',
      label: 'the Zoom Marketplace (not signed in yet)',
      resumeNavigateUrl: ZOOM_SIGNIN_URL,
      needsUser: true,
    };
  }

  // about:blank, the Zoom marketing home, or anywhere the user drifted to →
  // drive back to the builder to continue.
  return { step: 'elsewhere', label: 'a different page', resumeNavigateUrl: ZOOM_S2S_CREATE_URL, needsUser: false };
}
